import { useMemo } from 'react';
import {
  Alert, Button,
} from '@openedx/paragon';
import { RiCheckDoubleLine } from '@remixicon/react';
import { useIntl } from '@edx/frontend-platform/i18n';

import { useNotifications, useNotificationsRead } from '@blend-ed/blendxplatform/hooks';

import emptyNotificationSVG from '../../assets/empty-notification.svg';
import messages from './messages';
import { formatNotificationTime } from './utils';
import './Notifications.scss';

const Notifications = () => {
  const intl = useIntl();

  const {
    data, loading, error, refetch,
  } = useNotifications();
  const { markAsRead } = useNotificationsRead({ role: 'learner', refetch });

  const items = useMemo(() => (data?.userPushNotifications ?? []).map((n) => ({
    id: n.id,
    title: n.notification?.title ?? '',
    message: n.notification?.message ?? '',
    createdAt: n.notification?.createdAt ?? '',
    readStatus: Boolean(n.readStatus),
  })), [data?.userPushNotifications]);

  const hasUnread = items.some((item) => !item.readStatus);

  const handleMarkOne = (item) => {
    if (item.readStatus) { return; }
    markAsRead({ variables: { keyId: item.id, set: true } });
  };

  const handleMarkAll = () => {
    items
      .filter((item) => !item.readStatus)
      .forEach((item) => markAsRead({ variables: { keyId: item.id, set: true } }));
  };

  return (
    <div className="learner-notifications">
      <header className="learner-notifications__header">
        <h1 className="learner-notifications__title">
          {intl.formatMessage(messages.pageTitle)}
        </h1>
        <Button
          variant="tertiary"
          size="sm"
          iconAfter={RiCheckDoubleLine}
          onClick={handleMarkAll}
          disabled={!hasUnread}
        >
          {intl.formatMessage(messages.markAllAsRead)}
        </Button>
      </header>

      {error && (
        <Alert variant="danger" className="learner-notifications__alert">
          {intl.formatMessage(messages.loadError)}
        </Alert>
      )}

      {loading && items.length === 0 && (
        <div className="learner-notifications__loading" aria-hidden="true">
          <div className="learner-notifications__loading-row" />
          <div className="learner-notifications__loading-row" />
          <div className="learner-notifications__loading-row" />
          <div className="learner-notifications__loading-row" />
        </div>
      )}

      {!loading && items.length === 0 && !error && (
        <div className="learner-notifications__empty">
          <img
            src={emptyNotificationSVG}
            alt={intl.formatMessage(messages.emptyImageAlt)}
            className="learner-notifications__empty-image"
          />
          <div className="learner-notifications__empty-text">
            <h3 className="learner-notifications__empty-title">
              {intl.formatMessage(messages.emptyTitle)}
            </h3>
            <p className="learner-notifications__empty-description">
              {intl.formatMessage(messages.emptyDescription)}
            </p>
          </div>
        </div>
      )}

      {items.length > 0 && (
        <ul className="learner-notifications__list">
          {items.map((item) => {
            const itemClass = `learner-notifications__item${
              item.readStatus ? ' learner-notifications__item--read' : ''
            }`;
            return (
              <li key={item.id}>
                <button
                  type="button"
                  className={itemClass}
                  onClick={() => handleMarkOne(item)}
                  aria-label={
                    item.readStatus
                      ? item.title
                      : `${item.title} (unread)`
                  }
                >
                  {!item.readStatus && (
                    <span className="learner-notifications__bullet" aria-hidden="true" />
                  )}
                  <div className="learner-notifications__header-row">
                    <p className="learner-notifications__title-text">{item.title}</p>
                    <span className="learner-notifications__time">
                      {formatNotificationTime(item.createdAt)}
                    </span>
                  </div>
                  {item.message && (
                    <p className="learner-notifications__message">{item.message}</p>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Notifications;
